/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AuthStoreModel } from '../../../modules/loadbalancer/shared/models/authStore.model';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { ModulesEnum } from '../../enums';
import { UtilsService } from './utils.service';

@Injectable()
export class AuthStoreService {
    /**
     * Store the login details
     * @private
     */
    private store: any = {};

    constructor(
        private logService: LogService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Set the auth module.
     * @param userDetails
     * @param data
     */
    public setAuthModule(userDetails: any, data: any): void {
        const moduleAuth = this.formatModuleAuthData(userDetails, data);
        this.addModuleAuth(moduleAuth);
    }

    /**
     * Add module auth.
     * @param auth
     */
    public addModuleAuth(auth: AuthStoreModel): void {
        this.utilsService.createArrayIfNotExist(this.store, auth.module);
        this.store[auth.module].push(auth);
    }

    /**
     *
     * @param module
     * @param application
     */
    public setSelectedModule(module: ModulesEnum, application: any): void {
        if (Object.keys(this.store).length) {
            for (const model of this.store[module]) {
                if (model.module === module) {
                    model.selected = model.id === application.id;
                }
            }
        }

        this.setLocalStorageLoginDetails(application, module);
    }

    /**
     * Get portal auth.
     */
    public getCentralManagementAuth(): AuthStoreModel {
        return this.store[ModulesEnum.CENTRALISEDMANAGEMENT] && this.store[ModulesEnum.CENTRALISEDMANAGEMENT][0];
    }

    /**
     * Get module auth.
     * @param module
     * @param id
     */
    public getModuleAuth(module: ModulesEnum, id: string): AuthStoreModel {
        return this.store[module] && this.store[module].find((item: { id: string; }) => item.id === id);
    }

    /**
     * Get the type of module auth set.
     */
    public getSelectedModuleAuthType(): ModulesEnum {
        if (Object.keys(this.store).length) {
            for (const store in this.store) {
                if (this.store.hasOwnProperty(store)) {
                    const selected = this.store[store].find((item: { selected: any; }) => item.selected);
                    if (selected) {
                        return selected.module;
                    }
                }
            }
        } else {
            return this.getLocalStorageLoginDetails().module;
        }
    }

    /**
     * Used by APIs to get module login details.
     * @param module
     */
    public getSelectedModuleAuth(module: ModulesEnum): any {
        const loginDetails = this.store && this.store[module]
            ? this.store[module].find((item: { selected: any; }) => item.selected)
            : this.getLocalStorageLoginDetails();

        if (!Object.keys(loginDetails).length) {
            this.logService.error('AuthStoreService: getSelectedModuleAuth', 'No login data');
        }

        return loginDetails;
    }

    /**
     * Remove Module Auth by module and ID
     * @param module
     * @param id
     */
    public removeModuleAuth(module: ModulesEnum, id: string): void {
        for (let i = 0; i <= this.store[module].length; i++) {
            const mod = this.store[module][i];

            if (mod.id === id) {
                this.store[module].splice(i, 1);
            }
        }
    }

    /**
     * Clear the auth store
     */
    public clearAuthStore(): void {
        this.store = {};
        this.logService.warn('AuthStoreService: clearAuthStore', 'Auth Store cleared');
    }

    /**
     * Format module auth data.
     * @param userDetails
     * @param data
     */
    public formatModuleAuthData(
        userDetails: { applianceAddress: any; appliancePort: any; },
        data: { token: any; iss: any; language: any; }
    ): AuthStoreModel {
        const module = this.getModuleTypeFromJWT(data.token);

        return <AuthStoreModel>{
            module,
            iss: data.iss,
            ip: userDetails.applianceAddress,
            port: userDetails.appliancePort,
            jwt: data.token,
            selected: true,
            language: data.language
        };
    }

    /**
     * Get module type from JWT
     * @param token
     */
    public getModuleTypeFromJWT(token: string): ModulesEnum {
        const decodedToken: any = this.utilsService.decodeJWT(token);

        switch (decodedToken.iss) {
            case 'lbCentral':
                return ModulesEnum.CENTRALISEDMANAGEMENT;
            default:
                return ModulesEnum.LOADBALANCER;
        }
    }

    /**
     * Get local storage login details.
     * @private
     */
    private setLocalStorageLoginDetails(details: { user: any; }, module: ModulesEnum): void {
        const user: any = details.user;

        const formattedDetails = {
            ip: user.applianceAddress,
            port: user.appliancePort,
            jwt: user.JWT,
            module: module,
            selected: true
        };
        const userObject: string = JSON.stringify(formattedDetails);
        localStorage.setItem('lbUser', userObject);
    }

    /**
     * Get local storage login details.
     * @private
     */
    private getLocalStorageLoginDetails(): any {
        const lbUser: string = localStorage.getItem('lbUser');
        const userObject: any = JSON.parse(lbUser);

        return {
            ip: userObject.applianceAddress,
            port: userObject.appliancePort,
            jwt: userObject.JWT,
            module: this.getModuleTypeFromJWT(userObject.JWT),
            selected: true
        };
    }
}
